import React, { useEffect, useState } from 'react';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Modal from '@mui/material/Modal';
import {
    StyledUploaderDesc,
    StyledUploaderField,
    StyledUploaderFieldDesc,
    StyledUploaderFieldSelected,
    StyledUploaderFieldTitle,
    StyledUploaderFilesSize,
    StyledUploaderFilesWrapper,
    StyledUploaderModalWrapper,
    StyledUploaderTitle
} from "./UploadFileFormik.styled";
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FileItem from "./FileItem";
import PublishIcon from '@mui/icons-material/Publish';
import { useSnackbar } from 'notistack'
import { useTranslation } from "react-i18next";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {useAppDispatch, useAppSelector} from "../../../commons/store/hooks";
import { uploadFiles } from "features/ecrf/redux/elementDataSlice";
import { edUploading } from "features/ecrf/redux/elementDataSlice";
import { fetchCollection } from "features/ecrf/redux/elementResourceTransferSlice";
import { formatBytes } from "lib";

// ==============================================================
interface Props {
  open: boolean;
  setOpen: (_: boolean) => void;
  handleClose: () => void;
  element: any;
  ecrfId: string,
  data: any;
  status?: boolean;
  uploadLimit?: number;
  reload?: () => void;
}
// ==============================================================

const UploadFileModal = ({
  open, setOpen, handleClose, ecrfId, element,  data, status, uploadLimit = 2147483648, reload
}: Props) => {
  const dispatch = useAppDispatch();
  const isUploading = useAppSelector(edUploading);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [files, setFiles] = useState<any[]>([]);
  const [isUploaded, setIsUploaded] = useState(false);
  const [bucket, setBucket] = useState<any[]>([]);
  const [isDataFetching, setIsDataFetching] = useState(false);
  const [filesSize, setFilesSize] = useState(0);

  const closeModal = () => {
    setOpen(false);
    setFiles([]);
    setFilesSize(0);
    setIsUploaded(false);
    if (reload) {
      reload();
    }
  };

  const handleSelectFiles = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    let newFiles  = Array.from(e.target.files || []);

    const existingNames = files.map((f: any) => f.name);
    const filtered = newFiles.filter((f: any) => !existingNames.includes(f.name));

    if (filtered.length < newFiles.length) {
      enqueueSnackbar(t('Some files already exist in the list'), { variant: 'warning' });
    }

    const updatedFiles = [...files, ...filtered];
    const updatedSize = updatedFiles.reduce((acc, file) => acc + file.size, 0);

    updatedFiles && setFiles(updatedFiles);
    updatedSize && setFilesSize(updatedSize);
    setIsUploaded(false);
  };

  const removeFromList = (name: string) => {
    const filtered = files.filter((file) => file.name !== name);
    const size = filtered.reduce((acc, file) => acc + file.size, 0);

    setFiles(filtered);
    setFilesSize(size);
  };

  const upload = async () => {
    if (filesSize > uploadLimit) return;

    try {
      const formData = new FormData();
      files.forEach((file, i) => formData.append(`file${i}`, file));

      await dispatch(uploadFiles({ ecrfId: ecrfId, elementId: element.id, params: formData }));
      await getFilesData(data.id);
      if (reload) {
        await reload();
      }

      setFiles([]);
      setIsUploaded(true);
    } catch (error: any) {
      enqueueSnackbar(error?.detail || t('Upload failed'), { variant: 'warning' });
    }
  };

  const getFilesData = async (id: string) => {
    try {
      setIsDataFetching(true);
      const response = await dispatch(fetchCollection({ elementId: id })).unwrap();
      setBucket(response);
    } catch (error: any) {
      enqueueSnackbar(error?.detail || t('Failed to download data'), { variant: 'warning' });
    } finally {
      setIsDataFetching(false);
    }
  };

  useEffect(() => {
    if (data?.id) {
      getFilesData(data.id);
    }
  }, [data]);

  return (
    <Modal
      style={{ overflowY: 'auto', width: "100%", display: 'grid', justifyItems: 'center' }}
      open={open}
    >
      <Box m={3} maxWidth={'600px'} p={3}>
        <StyledUploaderModalWrapper>
          <StyledUploaderTitle>
            {t('import-data')}
          </StyledUploaderTitle>

          <StyledUploaderDesc>
            {t('modal-file-upload-desc')}
          </StyledUploaderDesc>

          {/* <--- Initial upload view ---> */}
          {files && !data && files.length === 0 && (
            <>
              <Button component={'label'} style={{ all: 'unset', width: '100%' }} disabled={status}>
                <StyledUploaderField onChange={handleSelectFiles} component={'label'}>
                  <InsertDriveFileOutlinedIcon style={{ fontSize: '60px', color: '#929DA7' }} />
                  <StyledUploaderFieldTitle>
                    {t('select-files-here')}
                  </StyledUploaderFieldTitle>
                  <StyledUploaderFieldDesc>
                    {t('file-format')}: CSV
                  </StyledUploaderFieldDesc>
                  <StyledUploaderFieldDesc>
                    {t('max-files-size')}: 2GB
                  </StyledUploaderFieldDesc>
                  <Button
                    variant={'outlined'}
                    component={'label'}
                    style={{ marginTop: '15px' }}
                    disabled={status}
                  >
                    {t('select-files')}
                    <input multiple type={'file'} hidden />
                  </Button>
                </StyledUploaderField>
              </Button>
              <Button style={{ marginTop: '25px' }} onClick={handleClose} size={'small'}>
                {t('cancel')}
              </Button>
            </>
          )}

          {/* <--- Loaded files view ---> */}
          {!isUploaded && files?.length > 0 && (
            <StyledUploaderFieldSelected>
              <StyledUploaderFieldTitle>
                {files.length > 0 ? t('selected-files') : t('select-files')}
              </StyledUploaderFieldTitle>
              <StyledUploaderFilesWrapper>
                {[].slice.call(files).map((file: any, index) =>
                  <FileItem
                    key={index}
                    id={file.id}
                    name={file.name}
                    size={file.size}
                    removeRow={removeFromList}
                    isUploading={isUploading}
                  />
                )}

                <StyledUploaderFilesSize limit={filesSize > uploadLimit}>
                  {t('files-size-limit')}: {formatBytes(filesSize)} / {formatBytes(uploadLimit)}
                </StyledUploaderFilesSize>
              </StyledUploaderFilesWrapper>

              <Box mt={1} display='flex' gap={'20px'}>
                <Button onClick={handleClose} size={'small'}>
                  {t('cancel')}
                </Button>

                <Button
                  component={'label'}
                  onChange={handleSelectFiles}
                  startIcon={<AddRoundedIcon />}
                  size={'small'}
                  disabled={status}
                >
                  {t('upload-more')}
                  <input multiple type={'file'} hidden />
                </Button>

                <Button
                  disabled={isUploading || filesSize > uploadLimit || status}
                  component={'label'}
                  onClick={upload}
                  size={'small'}
                  startIcon={isUploading ? <CircularProgress size={14} /> : <PublishIcon />} color={'primary'}
                >
                  {isUploading ? t('importing') : t('import')}
                </Button>
              </Box>
            </StyledUploaderFieldSelected>
          )}

          {/* <--- View for positive upload status ---> */}
          {isUploaded && (
            <StyledUploaderFieldSelected style={{ marginBottom: '-50px' }}>
              <StyledUploaderTitle>
                {t('files-imported-successfully')}!
              </StyledUploaderTitle>
            </StyledUploaderFieldSelected>
          )}

          {/* List files */}
          {data && (
            <>
              {(files.length === 0) && (
                <StyledUploaderFieldSelected>
                  <StyledUploaderFieldTitle style={{ marginBottom: '10px' }}>
                    {bucket.length > 0 ? `${t('Files')} (${bucket.length})`: t('Add files')}
                  </StyledUploaderFieldTitle>

                  <StyledUploaderFilesWrapper>
                    {[].slice.call(bucket).map((file: any) =>
                      <FileItem
                        key={file.id}
                        id={file.id}
                        name={file.filename}
                        size={file.size}
                        link={file.link}
                        dataId={data.id}
                        update={() => getFilesData(data.id)}
                      />
                    )}

                    {/* <--- Spinner when data is fetching --- >*/}
                    {isDataFetching && (
                      <Box m={3} display={'grid'} justifyContent={'center'}>
                        <CircularProgress />
                      </Box>
                    )}
                  </StyledUploaderFilesWrapper>

                  <Box mt={1} display='flex' gap={'20px'}>
                    <Button onClick={() => closeModal()} size={'small'}>
                      {t('close')}
                    </Button>

                    <Button
                      component={'label'}
                      onChange={handleSelectFiles}
                      startIcon={<AddRoundedIcon />}
                      size={'small'}
                      disabled={status}
                    >
                      {t('upload-more')}
                      <input multiple type={'file'} hidden />
                    </Button>
                  </Box>
                </StyledUploaderFieldSelected>
              )}
            </>
          )}
        </StyledUploaderModalWrapper>
      </Box>
  </Modal>
  );
}

export default UploadFileModal;